import React from 'react'

import { Box, Stack, useTheme } from '@mui/material'
import { Skeleton } from '@repo/ui'

import { CHANNEL_COLUMN_GAP, CHANNEL_COLUMN_WIDTH, PageContainer } from 'components/UI'
import { PROSE_MAX_WIDTH } from 'constants/layout'

/**
 * One preference row placeholder — title + description bars on the left, one
 * switch-sized bar per channel column on the right. Uses the SAME column width
 * and gap constants as the real row, so the columns can't drift apart.
 */
const RowSkeleton = () => (
  <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ py: 2 }}>
    <Stack gap={1}>
      <Skeleton variant='text' width={140} height={20} />
      <Skeleton variant='text' width={220} height={16} />
    </Stack>
    <Stack direction='row' spacing={CHANNEL_COLUMN_GAP} alignItems='center'>
      {[0, 1].map(i => (
        <Box key={i} sx={{ width: CHANNEL_COLUMN_WIDTH, display: 'flex', justifyContent: 'center' }}>
          <Skeleton variant='rounded' width={40} height={24} />
        </Box>
      ))}
    </Stack>
  </Stack>
)

/** One address row placeholder (email / phone) — label + value on the left, a button-sized bar on the right. */
const PhoneRowSkeleton = () => (
  <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ py: 1 }}>
    <Stack gap={1}>
      <Skeleton variant='text' width={120} height={20} />
      <Skeleton variant='text' width={200} height={16} />
    </Stack>
    <Skeleton variant='rounded' width={88} height={32} />
  </Stack>
)

/**
 * Loading placeholder for the Notifications page. Mirrors the real layout 1:1
 * — back link, title, description, phone row, then the single preference grid
 * (column header + three rows) — so there is zero layout shift when the profile
 * loads (working-conventions §19).
 */
export const NotificationsSkeleton = () => {
  const theme = useTheme()
  return (
    <Stack sx={{ background: theme.palette.background.paper, minHeight: '100vh', pt: { xs: 4, md: 6 }, pb: { xs: 4, md: 8 } }}>
      {/* Same container + reading measure as the real page (§19 footprint parity). */}
      <PageContainer>
      <Box sx={{ width: '100%', maxWidth: PROSE_MAX_WIDTH, mx: 'auto' }}>
        {/* Back link + title */}
        <Skeleton variant='text' width={120} height={16} sx={{ mb: 3 }} />
        <Skeleton variant='text' width={180} height={28} sx={{ mb: 2 }} />

        {/* Description */}
        <Skeleton variant='text' width='90%' height={16} sx={{ mb: 1 }} />
        <Skeleton variant='text' width='60%' height={16} sx={{ mb: 3 }} />

        {/* Email + phone address rows */}
        <PhoneRowSkeleton />
        <PhoneRowSkeleton />

        <Box sx={{ height: 4, backgroundColor: theme.palette.grey[50], my: 3, borderRadius: '8px' }} />

        {/* Column header (Email / SMS) */}
        <Stack direction='row' justifyContent='flex-end' sx={{ pb: 1 }}>
          <Stack direction='row' spacing={CHANNEL_COLUMN_GAP}>
            {[32, 28].map((w, i) => (
              <Box key={i} sx={{ width: CHANNEL_COLUMN_WIDTH, display: 'flex', justifyContent: 'center' }}>
                <Skeleton variant='text' width={w} height={16} />
              </Box>
            ))}
          </Stack>
        </Stack>

        {/* Scans / Transfers / Marketing */}
        <RowSkeleton />
        <RowSkeleton />
        <RowSkeleton />
      </Box>
      </PageContainer>
    </Stack>
  )
}
